import { Anchor, Center, Drawer, Group, Loader, Stack, Text } from '@mantine/core'
import { Link } from 'react-router-dom'
import { EmptyState, MetaList } from '@lighthouse/design'
import { IconQuestionMark } from '@tabler/icons-react'
import { useSignal } from '../../hooks/useSignals'
import { NOT_MEASURED, errorKind, whenLabel } from '../../utils/format'
import { SignalErrorState, SignalHandlingChip, SignalSeverity, SignalStateLabel } from './SignalBits'
import { SignalHandlingPanel } from './SignalHandlingPanel'
import {
  reasonText, resolutionText, signalLinks, signalTitle, subjectKindLabel,
} from './signalFormat'
import type { SignalDetail } from '../../types/signals'

/** Where a signal opens when the caller names no Inbox URL of its own. */
const DEFAULT_SIGNAL_HREF = (id: string) => `/signals?signal=${encodeURIComponent(id)}`

/**
 * One signal in full, opened by `?signal={id}`: every served field, who has it, and one timeline of the detector's
 * transitions and people's acts.
 *
 * The only controls are handling's (SignalHandlingPanel): acknowledge, assign, release — behind the handling gate,
 * on an open signal. None of them ends a signal: there is no resolve and no snooze, and the way to end one is still
 * to fix its subject, on the page the links go to; the detector notices on its next pass.
 */
export function SignalDrawer({ signalId, onClose, signalHref = DEFAULT_SIGNAL_HREF }: {
  signalId: string | null
  onClose: () => void
  /** The Inbox URL for another signal, its other filters kept — the previous occurrence's link. */
  signalHref?: (id: string) => string
}) {
  return (
    <Drawer
      opened={signalId !== null}
      onClose={onClose}
      position="right"
      size="xl"
      title={<Text fw={600}>Signal</Text>}
    >
      {/* Keyed on the id, so moving to another signal remounts the body rather than flashing the last one. */}
      {signalId && <SignalDrawerBody key={signalId} id={signalId} signalHref={signalHref} />}
    </Drawer>
  )
}

/**
 * The drawer's contents. Exported for the render tests: Mantine's Drawer portals its children, and a portal
 * renders nothing under react-dom/server.
 */
export function SignalDrawerBody({ id, signalHref = DEFAULT_SIGNAL_HREF }: { id: string; signalHref?: (id: string) => string }) {
  const { data, isLoading, isError, error } = useSignal(id)

  if (isError) {
    if (errorKind(error) === 'not-found') {
      return (
        <div data-signal-detail="missing">
          <EmptyState
            icon={<IconQuestionMark size={28} stroke={1.5} />}
            title="No such signal"
            message={
              'Nothing in the store has this id. A signal that ended is kept for the resolved-signal retention '
              + 'window (Etl:TelemetryRetention:SignalRetentionDays) and then trimmed; an open one never is.'
            }
          />
        </div>
      )
    }
    return <SignalErrorState error={error} />
  }

  if (isLoading || !data) {
    return (
      <Center py="xl" data-signal-detail="loading">
        <Loader size="sm" />
      </Center>
    )
  }

  return <SignalDetailView signal={data} signalHref={signalHref} />
}

function orDash(value: string | null | undefined) {
  return value?.trim() ? value : NOT_MEASURED
}

function SignalDetailView({ signal, signalHref }: { signal: SignalDetail; signalHref: (id: string) => string }) {
  const links = signalLinks(signal)
  return (
    <Stack gap="md" data-signal-detail="ready">
      <Stack gap={4}>
        <Group gap="xs">
          <SignalSeverity severity={signal.severity} />
          <Text fw={600}>{signalTitle(signal)}</Text>
        </Group>
        <Text size="sm" style={{ overflowWrap: 'anywhere' }}>{orDash(signal.subjectLabel)}</Text>
        <Group gap="md" align="center">
          <SignalStateLabel signal={signal} />
          <SignalHandlingChip signal={signal} />
        </Group>
      </Stack>

      {links.length > 0 && (
        <Group gap="md">
          {links.map((link) => (
            <Anchor key={link.to} component={Link} to={link.to} size="sm">{link.label} →</Anchor>
          ))}
        </Group>
      )}

      <MetaList
        items={[
          { label: 'Rule', value: `${signalTitle(signal)} (${signal.ruleId})` },
          { label: 'Subject kind', value: subjectKindLabel(signal.subjectKind) },
          { label: 'Subject key', value: orDash(signal.subjectKey), mono: true },
          { label: 'Subject id', value: orDash(signal.subjectRefId), mono: true },
          {
            label: 'Condition began',
            value: signal.conditionSince ? whenLabel(signal.conditionSince) : `${NOT_MEASURED} not recorded by its input`,
          },
          { label: 'First observed', value: whenLabel(signal.firstObservedAt) },
          { label: 'Evidence as of', value: signal.evidenceAsOf ? whenLabel(signal.evidenceAsOf) : `${NOT_MEASURED} not recorded by its input` },
          { label: 'Last checked by the detector', value: whenLabel(signal.lastCheckedAt) },
          { label: 'Observation', value: signal.observation === 'Observed' ? 'Observed on the last pass' : 'Not confirmed on the last pass' },
          { label: 'Unconfirmed since', value: signal.unobservedSince ? whenLabel(signal.unobservedSince) : NOT_MEASURED },
          { label: 'Why unconfirmed', value: signal.unobservedReason ? `${reasonText(signal.unobservedReason)}.` : NOT_MEASURED },
          { label: 'Observed value', value: orDash(signal.observedValue) },
          { label: 'Threshold', value: orDash(signal.threshold) },
          { label: 'Ended', value: signal.isOpen ? 'Still open' : whenLabel(signal.resolvedAt) },
          { label: 'How it ended', value: signal.isOpen ? NOT_MEASURED : resolutionText(signal.resolution, signal.subjectKind) },
        ]}
      />

      <SignalHandlingPanel signal={signal} signalHref={signalHref} />
    </Stack>
  )
}
